import type { ReactNode } from 'react';
import { DOCTOR_SLOT_SUBTYPE } from '../../../enums/doctorslot.enum';
import { PRODUCT_CODE } from '../../../enums/productCode.enum';
import { createPaymentRedirect } from '../../../helper/createPaymentRedirect';
import { handlePaymentRedirect } from '../../../helper/handlePaymentRedirect';
import { setPendingPayment } from '../../../helper/paymentPendingStorage';
import AppointmentService from '../../../services/Appointment.service';
import PharmacyService from '../../../services/Pharmacy.service';
import UserService from '../../../services/User.service';
import { useAppSelector } from '../../../store/hooks';
import { selectUserInformation } from '../../../store/reducers';
import { selectCurrentProduct } from '../../../store/reducers/productReducers';
import { ProgressStep, ProgressSteps } from '../ProgressSteps/ProgressSteps';
import { Checkout } from './Checkout';

type AppointmentCheckoutWrapperProps = {
  appointmentInfo: any;
  productCode: PRODUCT_CODE;
};

export function AppointmentCheckout({ appointmentInfo, productCode }: AppointmentCheckoutWrapperProps) {
  const { pharmacyId, products, patientNotes } = appointmentInfo;

  const userInformation = useAppSelector(selectUserInformation);
  const patientId = parseInt(userInformation.id, 10);
  const existingPharmacyId = userInformation.patientPharmacyRelation.id;

  const fetchedProduct = useAppSelector(selectCurrentProduct);
  const offlineAppointmentCodes: PRODUCT_CODE[] = [
    PRODUCT_CODE.FIRST_APPOINTMENT_OFFLINE_SHORT,
    PRODUCT_CODE.FIRST_APPOINTMENT_OFFLINE_LONG,
    PRODUCT_CODE.FIRST_APPOINTMENT_OFFLINE_MISSED_SHORT,
    PRODUCT_CODE.FIRST_APPOINTMENT_OFFLINE_MISSED_LONG,
    PRODUCT_CODE.FOLLOW_UP_APPOINTMENT_OFFLINE,
    PRODUCT_CODE.FOLLOW_UP_APPOINTMENT_OFFLINE_MISSED,
    PRODUCT_CODE.RESERVATION_FIRST_APPOINTMENT_OFFLINE_SHORT,
    PRODUCT_CODE.RESERVATION_FIRST_APPOINTMENT_OFFLINE_LONG,
    PRODUCT_CODE.RESERVATION_FOLLOW_UP_APPOINTMENT_OFFLINE,
  ];

  const firstAppointmentCodes: PRODUCT_CODE[] = [
    PRODUCT_CODE.FIRST_APPOINTMENT_ONLINE_SHORT,
    PRODUCT_CODE.FIRST_APPOINTMENT_ONLINE_LONG,
    PRODUCT_CODE.FIRST_APPOINTMENT_ONLINE_MISSED_SHORT,
    PRODUCT_CODE.FIRST_APPOINTMENT_ONLINE_MISSED_LONG,
    PRODUCT_CODE.FIRST_APPOINTMENT_OFFLINE_SHORT,
    PRODUCT_CODE.FIRST_APPOINTMENT_OFFLINE_LONG,
    PRODUCT_CODE.FIRST_APPOINTMENT_OFFLINE_MISSED_SHORT,
    PRODUCT_CODE.FIRST_APPOINTMENT_OFFLINE_MISSED_LONG,
    PRODUCT_CODE.RESERVATION_FIRST_APPOINTMENT_ONLINE_SHORT,
    PRODUCT_CODE.RESERVATION_FIRST_APPOINTMENT_ONLINE_LONG,
    PRODUCT_CODE.RESERVATION_FIRST_APPOINTMENT_OFFLINE_SHORT,
    PRODUCT_CODE.RESERVATION_FIRST_APPOINTMENT_OFFLINE_LONG,
  ];

  const isOffline = offlineAppointmentCodes.includes(productCode);
  const isFirstAppointment = firstAppointmentCodes.includes(productCode);

  const appointmentTime = `${new Date(appointmentInfo.selectedDate).toLocaleString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })}, ${new Date(appointmentInfo.selectedSlot.start).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })} - ${new Date(appointmentInfo.selectedSlot.end).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })} Uhr`;

  const durationMinutes = Number(appointmentInfo?.firstAppointmentDuration);
  const details: Array<{ label: string; value: string | ReactNode }> = [
    { label: 'Terminart', value: fetchedProduct?.label },
  ];

  if (isFirstAppointment && !Number.isNaN(durationMinutes)) {
    details.push({ label: 'Dauer', value: `${durationMinutes} Minuten` });
  }

  details.push(
    { label: 'Datum / Uhrzeit', value: appointmentTime },
    {
      label: 'Arzt / Ärztin',
      value: appointmentInfo.doctorName,
    }
  );

  const handleSubmit = async (couponCode?: string): Promise<any> => {
    try {
      if (pharmacyId && existingPharmacyId !== pharmacyId) {
        const pharmacyResponse = await PharmacyService.savePatientPharmacyRelation({ pharmacyId, patientId });
        if (pharmacyResponse.error) {
          console.error('Error setting pharmacy to', pharmacyId, pharmacyResponse);

          return { success: false, error: pharmacyResponse };
        }
      }

      const doctorResponse = await UserService.assignDoctorWithType(
        parseInt(userInformation.id, 10),
        parseInt(appointmentInfo.doctorId, 10),
        'main'
      );
      if (doctorResponse.error) {
        console.error('Error setting doctor', doctorResponse);

        return { success: false, error: doctorResponse };
      }

      const redirectUrl = createPaymentRedirect();
      const appointmentResponse = await AppointmentService.createAppointment({
        patientNotes: `${products.join('\n')}\n\n${patientNotes}`.trim(),
        doctorName: appointmentInfo.doctorName,
        doctorId: appointmentInfo.doctorId,
        startDate: appointmentInfo.selectedSlot.start,
        endDate: appointmentInfo.selectedSlot.end,
        practiceId: appointmentInfo?.practiceId ?? appointmentInfo?.location?.id,
        type: isOffline ? DOCTOR_SLOT_SUBTYPE.OFFLINE : DOCTOR_SLOT_SUBTYPE.ONLINE,
        appointmentType: isFirstAppointment ? 'firstAppointment' : 'followUpAppointment',
        comment: 'Von Patient erstellt',
        couponCode: couponCode,
        redirectUrl,
      });

      if (appointmentResponse.error) {
        console.error('Error creating appointment', appointmentResponse);

        return { success: false, error: appointmentResponse };
      } else {
        const paymentUrl = appointmentResponse.data?.data?.redirectUrl;

        if (paymentUrl) {
          setPendingPayment({
            startedAt: Date.now(),
            sourcePath: `${window.location.pathname}${window.location.search}`,
            novalnetUrl: paymentUrl,
            contextType: 'appointment',
          });
          handlePaymentRedirect(paymentUrl);
        } else {
          window.location.href = redirectUrl;
        }

        return { success: true };
      }
    } catch (error) {
      console.error('Error creating appointment', error);

      return { success: false, error };
    }
  };

  return (
    <>
      <div className='max-w-[550px] mx-auto mt-3 mb-4'>
        <ProgressSteps>
          {isOffline && isFirstAppointment ? <ProgressStep title='Standort' active /> : null}
          <ProgressStep title='Datum' active />
          <ProgressStep title='Uhrzeit' active />
          <ProgressStep title='Information' active />
          <ProgressStep title='Bestätigung' active />
          <ProgressStep title='Zahlung' />
        </ProgressSteps>
      </div>
      <Checkout
        productCode={productCode}
        itemDetailsTitle='Ihre Termindetails'
        itemDetails={details}
        contractDoctorStamp={appointmentInfo.doctorStamp}
        handleSubmit={handleSubmit}
      />
    </>
  );
}
